import React from "react";
import { useNavigate } from "react-router-dom";
import { MessageSquare, BookOpen, User as UserIcon } from "lucide-react";

/**
 * Item 2 — Match-result card used everywhere the app searches for
 * users (top header search, lounge search, messages tab search).
 *
 * Shows the user's avatar + name + @handle and offers TWO CTAs:
 *   1. See lounge posts  → /lounge?user_id={id}   (filtered feed)
 *   2. Message           → /messages?with={id}    (opens DM thread)
 *
 * The user's own profile page is intentionally NOT linked from here —
 * per product spec the profile stays private (edit-only).
 */
export default function UserMatchCard({ user, onNavigate }) {
  const nav = useNavigate();
  const go = (path) => {
    if (onNavigate) onNavigate(path);
    nav(path);
  };
  const handle = (user.handle || "").replace(/^@/, "");
  const initial = (user.name || handle || "?").trim().slice(0, 1).toUpperCase();
  return (
    <div
      data-testid={`user-match-card-${user.id}`}
      className="rounded-xl bg-tb-cream-2 border-2 border-tb-cream-3 hover:border-tb-gold p-3 flex items-center gap-3"
    >
      {user.avatar_url ? (
        <img
          src={user.avatar_url}
          alt=""
          className="w-12 h-12 rounded-full object-cover bg-tb-cream-3 border-2 border-tb-cream-3 flex-shrink-0"
        />
      ) : (
        <div className="w-12 h-12 rounded-full bg-tb-navy text-tb-gold-bright border-2 border-tb-cream-3 flex-shrink-0 flex items-center justify-center font-serif-it text-lg">
          {initial || <UserIcon className="w-5 h-5" />}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="font-serif-it text-tb-navy text-lg leading-tight truncate">
          {user.name || "Traveler"}
        </div>
        {handle && (
          <div className="font-mono text-tb-gold text-sm truncate">
            @{handle}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          type="button"
          data-testid={`user-match-lounge-${user.id}`}
          onClick={() => go(`/lounge?user_id=${encodeURIComponent(user.id)}`)}
          className="px-2.5 py-1.5 rounded-full bg-white border-2 border-tb-navy/15 hover:border-tb-navy/40 text-tb-navy font-hand text-xs inline-flex items-center gap-1 tb-tap"
          title="See lounge posts"
          aria-label={`See lounge posts by ${user.name || handle}`}
        >
          <BookOpen className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">posts</span>
        </button>
        <button
          type="button"
          data-testid={`user-match-message-${user.id}`}
          onClick={() => go(`/messages?with=${encodeURIComponent(user.id)}`)}
          className="px-2.5 py-1.5 rounded-full bg-tb-navy border-2 border-tb-gold/60 hover:border-tb-gold text-tb-gold-bright font-hand text-xs inline-flex items-center gap-1 tb-tap"
          title="Message"
          aria-label={`Message ${user.name || handle}`}
        >
          <MessageSquare className="w-3.5 h-3.5" />
          <span>message</span>
        </button>
      </div>
    </div>
  );
}
